import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { COUNTRY_CODES } from "canbook-data/util/country-codes";

export type CountryCodeDropdownProps = {
  value: string;
  onChange: (dialCode: string) => void;
  containerClassName?: string;
  buttonClassName?: string;
  dropdownClassName?: string;
};

type DropdownDirection = "up" | "down";

export default function CountryCodeDropdown({
  value,
  onChange,
  containerClassName = "relative w-28",
  buttonClassName = "border border-outline bg-container-low py-3 px-3 rounded-xl w-full text-left flex items-center justify-between text-font-primary outline-none focus:border-primary",
  dropdownClassName = "absolute left-0 w-64 max-h-80 overflow-auto rounded-xl border border-outline bg-surface shadow-lg z-50 flex flex-col",
}: CountryCodeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownListRef = useRef<HTMLDivElement>(null);
  const [dropdownDirection, setDropdownDirection] =
    useState<DropdownDirection>("down");
  const [isDirectionResolved, setIsDirectionResolved] = useState(false);

  const selected =
    COUNTRY_CODES.find((c) => c.dialCode === value) ?? COUNTRY_CODES[0];

  const filteredCountries = COUNTRY_CODES.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.dialCode.includes(searchQuery),
  );

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownDirection("down");
      setIsDirectionResolved(false);
      return;
    }

    const calculateDirection = () => {
      if (!containerRef.current || !dropdownListRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownListRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - containerRect.bottom;
      const spaceAbove = containerRect.top;
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight + 20) {
        setDropdownDirection("up");
      } else {
        setDropdownDirection("down");
      }
    };

    const scheduled = () =>
      requestAnimationFrame(() => {
        calculateDirection();
        setIsDirectionResolved(true);
      });
    scheduled();
    window.addEventListener("resize", scheduled);
    window.addEventListener("scroll", scheduled, true);

    return () => {
      window.removeEventListener("resize", scheduled);
      window.removeEventListener("scroll", scheduled, true);
    };
  }, [isOpen, filteredCountries.length]);

  return (
    <div className={containerClassName} ref={containerRef}>
      <button
        type="button"
        className={buttonClassName}
        onClick={() => {
          setIsDirectionResolved(false);
          setIsOpen((prev) => !prev);
        }}
      >
        <span className="flex items-center gap-2">
          <span>{selected.flag}</span>
          <span>{selected.dialCode}</span>
        </span>
        <span
          className="ml-2 text-[10px] transition-transform duration-200"
          style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
        >
          ▼
        </span>
      </button>
      {isOpen && (
        <div
          ref={dropdownListRef}
          className={`${dropdownClassName} ${
            dropdownDirection === "up" ? "bottom-full mb-1" : "top-full mt-1"
          }`}
          style={!isDirectionResolved ? { visibility: "hidden" } : undefined}
        >
          <div className="sticky top-0 bg-surface p-2 border-b border-outline z-10">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search country or code..."
              className="w-full px-3 py-2 text-sm bg-container-low border border-outline rounded-lg focus:border-primary outline-none text-font-primary placeholder:text-font-disabled"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setIsOpen(false);
                  setSearchQuery("");
                }
              }}
            />
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 py-1">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.iso2}
                  type="button"
                  className={`w-full px-4 py-2.5 flex items-center gap-3 text-left text-font-primary hover:bg-container-normal transition-colors
                    ${country.dialCode === value ? "bg-container-low" : ""}
                  `}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onChange(country.dialCode);
                    setIsOpen(false);
                    setSearchQuery("");
                  }}
                >
                  <span className="text-xl">{country.flag}</span>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-sm font-medium truncate">
                      {country.name}
                    </span>
                    <span className="text-xs text-font-secondary">
                      {country.dialCode}
                    </span>
                  </div>
                  {country.dialCode === value && (
                    <span className="text-primary text-xs">✓</span>
                  )}
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-font-disabled text-sm">
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
